import { useState } from 'react';
import type { Beer } from '../api/types';
import { BeerStatusBadge } from './BeerStatusBadge';
import '../styles/beers.css';

interface BeerCardGridProps {
  beers: Beer[];
  onBeerClick: (beer: Beer) => void;
}

function BeerArtwork({ beer }: { beer: Beer }) {
  const [imgError, setImgError] = useState(false);
  if (beer.artworkBlobUri && !imgError) {
    return (
      <img
        src={beer.artworkBlobUri}
        alt={beer.name}
        onError={() => setImgError(true)}
        className="beers-card-artwork"
      />
    );
  }
  const initial = beer.name.trim()[0]?.toUpperCase() ?? '?';
  return (
    <div className="beers-card-artwork-fallback">
      {initial}
    </div>
  );
}

function StyleBadge({ style }: { style: string }) {
  return (
    <span className="beers-style-badge">
      {style}
    </span>
  );
}

function BeerCard({ beer, onClick }: { beer: Beer; onClick: () => void }) {
  const titleClassName = beer.status === 'retired'
    ? 'beers-card-title beers-card-title-retired'
    : 'beers-card-title';

  return (
    <button onClick={onClick} className="beers-card">
      <BeerArtwork beer={beer} />
      <div className="beers-card-body">
        <div className="beers-card-top">
          <div className={titleClassName}>{beer.name}</div>
          <BeerStatusBadge status={beer.status} />
        </div>
        <div className="beers-card-meta-row">
          <StyleBadge style={beer.style} />
          {beer.abv != null && (
            <span className="mono beers-card-abv">
              {beer.abv}% ABV
            </span>
          )}
        </div>
        {beer.tags.length > 0 && (
          <div className="beers-card-tags">
            {beer.tags.map(t => (
              <span key={t} className="beers-card-tag">{t}</span>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}

export function BeerCardGrid({ beers, onBeerClick }: BeerCardGridProps) {
  return (
    <div className="beers-card-grid">
      {beers.map(b => (
        <BeerCard key={b.id} beer={b} onClick={() => onBeerClick(b)} />
      ))}
    </div>
  );
}
